import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {UnassignProductAccountEntitlementsTool} from 'src/modules/bulk-update';
import {NoResultPage, PreparedEntitlementsView, ProductResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {
    accountsEntitlementsControlHeader,
    accountsEntitlementsLoadingContainer,
    accountsEntitlementsTools,
} from '../account-entitlements.module.styl';

import {McpEntitlementsPagination} from './components/mcp-entitlements-pagination';
import {McpResourceNameHeader} from './components/mcp-resource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SECTION_NAME,
    SELECTION_KEY,
} from './constants';
import {useGetProductAccountEntitlements} from './hooks/useGetProductAccountEntitlements';
import {mcpEntitlementsStateActions} from './slices/mcp-entitlements-state.slice';
import {mcpEntitlementsActions} from './slices/mcp-entitlements.slice';


type Props = {
    accountId: number,
};

export const McpEntitlementsTab = ({accountId}: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {canEditAccountMcp} = useAdminAccessRight();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetProductAccountEntitlements({accountId});
    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(mcpEntitlementsActions.clearData());
            dispatch(mcpEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const redirectToAssignPage = useCallback(() => {
        dispatch(mcpEntitlementsStateActions.setFilters({}));
        navigate(`/profile/account/${accountId}/assign-product-resources`);
    }, [accountId, navigate, dispatch]);

    const refreshResults = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(mcpEntitlementsActions.requireRefresh());
    }, [dispatch]);

    const filterResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(mcpEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const noAccountResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <>
            <ServerResponseHandler serverError={loadingError} sectionName={SECTION_NAME}>
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    <StickyElement>
                        <div className={accountsEntitlementsControlHeader}>
                            <Group justify="space-between">
                                <ProductResourceSuggest
                                    onSearch={filterResources}
                                    accountId={accountId}
                                    id="mcp-resource-suggest"
                                />
                                <Group>
                                    <Group alignItems="center">
                                        <Label>Expand Roles</Label>
                                        <StateButton
                                            {...{
                                                name: 'mcp-expand-roles',
                                                disabled: isRolesDisabled,
                                                checked: isRolesExpanded,
                                                onChange: toggleRolesExpansion,
                                            }}
                                        />
                                    </Group>
                                    {canEditAccountMcp(accountId) && (
                                        <Button variant="Type.PRIMARY" onClick={redirectToAssignPage}>
                                            Assign Resource(s)
                                        </Button>
                                    )}
                                </Group>
                            </Group>
                        </div>
                    </StickyElement>
                    <BoxLoading loading={!result}>
                        <div className={accountsEntitlementsLoadingContainer}>
                            {result?.data.length ? (
                                <PreparedEntitlementsView
                                    resourceFilter={
                                        (cacheKeys.filters.resourceQuery as
                                            string | undefined) || ''
                                    }
                                    dataTableId="mcp-account-entitlements"
                                    rows={result.data}
                                    selectionKey={SELECTION_KEY}
                                    customOffset={137}
                                    expandedRoleIds={expandedRoleIds}
                                    setExpandedRoleIds={setExpandedRoleIds}
                                    readonly={!canEditAccountMcp(accountId)}
                                    toolRibbonClassName={accountsEntitlementsTools}
                                    getColumnWidthByColumnId={getColumnWidthByColumnId}
                                    onStopResize={onStopResize}
                                    paginationStripe={(
                                        <McpEntitlementsPagination
                                            totalCount={result.totalCount}
                                        />
                                    )}
                                    shortHeader={<McpResourceNameHeader/>}>
                                    {() => (
                                        <UnassignProductAccountEntitlementsTool
                                            accountId={accountId}
                                            selectionKey={SELECTION_KEY}
                                            onDone={refreshResults}
                                        />
                                    )}
                                </PreparedEntitlementsView>
                            ) : noAccountResultMessage}
                        </div>
                    </BoxLoading>
                </ErrorBoundary>
            </ServerResponseHandler>
        </>
    );
};
